/* The seam, in React. One DataSource for the whole app, handed down by context.
 *
 * A screen asks for it with useSource() and never learns which implementation
 * answered. Swapping SeedSource for an ApiSource is a change to this file and to
 * nothing else, which is the property the whole architecture is built to keep. */
import {
  createContext, useContext, useEffect, useRef, useState, type ReactNode,
} from 'react';
import { seesCommercials } from '../seats.js';
import type { DataSource } from './contract.js';
import { SeedSource } from './SeedSource.js';
import { ApiSource } from './ApiSource.js';

const Ctx = createContext<DataSource | null>(null);

/* WHETHER THIS IS THE SEEDED GYM. Exactly one thing reads it: the front door, which
   lists four seats and one password on the demo and asks for an email and a password
   against a server. No screen may branch on it - the entire point of the seam is
   that they cannot tell - and the sign-in card is not a screen, it is the thing that
   decides which gym there is. */
const IsDemo = createContext(true);
export function useIsDemo(): boolean { return useContext(IsDemo); }

export function useSource(): DataSource {
  const v = useContext(Ctx);
  if (!v) throw new Error('useSource() outside a SourceProvider');
  return v;
}

/* WHICH SOURCE, and it is the ONE decision that distinguishes the demo from the
   product. Everything else in the app is identical.
 *
 *   VITE_API_BASE  set at build time  -> the server at that path
 *   ?api=/v2/api   in the URL         -> the same, for trying one out
 *   neither                            -> the seeded gym
 *
 * The query-string form exists so a build can be pointed at a server without being
 * rebuilt, and it is deliberately explicit: there is no sniffing, no "try the API
 * and fall back". A fallback is how the previous build came up signed in as an
 * invented coach on a box holding a real gym's data. */
function chooseSource(): DataSource {
  const fromUrl = new URLSearchParams(window.location.search).get('api');
  const base = fromUrl ?? (import.meta.env['VITE_API_BASE'] as string | undefined) ?? '';
  return base ? new ApiSource({ base }) : new SeedSource();
}

export function SourceProvider({ children, source }: {
  children: ReactNode;
  /* AN INJECTION POINT FOR THE SEAM, which had none.
   *
   * The whole architecture rests on the app not knowing which source is behind it,
   * and the provider still chose one for itself with no way to say otherwise. That
   * is fine in the app, where chooseSource() is exactly right, and it makes the
   * seam untestable: a test that wants to render a screen AS THE OWNER cannot,
   * because the provider builds its own source signed in as somebody else.
   *
   * It cost a real hour. The rendered seat test asserted that the floor sees no
   * numbers and passed, and the same assertion for the Owner failed - not because
   * the gate was wrong but because every seat in the test was the default one.
   * Passing for the wrong reason is worse than failing. */
  source?: DataSource;
}) {
  /* Built once. A source rebuilt on every render regenerates the gym, so every
     keystroke would deal a different roster. */
  const ref = useRef<DataSource>(undefined);
  if (!ref.current) ref.current = source ?? chooseSource();
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let alive = true;
    void ref.current!.init().then(() => { if (alive) setReady(true); });
    return () => { alive = false; };
  }, []);

  if (!ready) return null;
  return (
    <Ctx.Provider value={ref.current}>
      <IsDemo.Provider value={ref.current instanceof SeedSource}>{children}</IsDemo.Provider>
    </Ctx.Provider>
  );
}

/* One async helper, so no screen writes the same useEffect five times. Nothing
   clever: no cache, no retry, no invalidation. When those are needed they are needed
   once, here, and not in twelve screens. */
export function useAsync<T>(run: () => Promise<T>, deps: unknown[]): {
  data: T | null; loading: boolean; error: Error | null;
} {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  useEffect(() => {
    let alive = true;
    setLoading(true);
    /* Promise.resolve().then(run), NOT run().then(...). A source method declared as
       returning a Promise can still throw SYNCHRONOUSLY, and when one did the throw
       escaped the effect entirely and React unmounted the whole tree: a blank white
       page instead of "could not read the roster". The screen must survive a source
       that misbehaves, because the source is the half most likely to. */
    Promise.resolve().then(run).then(
      (v) => { if (alive) { setData(v); setError(null); setLoading(false); } },
      (e: Error) => { if (alive) { setError(e); setLoading(false); } },
    );
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return { data, loading, error };
}

/* WHETHER THIS SEAT SEES WHAT A MEMBER PAYS.
 *
 * The source has already redacted the fields, so a screen reading this is only
 * deciding whether to draw the furniture: a Plan column of dashes is worse than no
 * Plan column, and a Sales tab of six blanks reads as a broken import rather than
 * as a rule. One hook, so twelve screens do not each re-derive it and drift. */
/* WHO SEES A NUMBER AT ALL (PRD 4).
 *
 * "Owner and Manager see the numbers. Support Coach and Coach get the same
 * worst-first order and the same reason in plain words, with NO NUMBER AND NO TIER
 * DOT. The ranking is the useful part; the number invites the floor to treat
 * people as figures."
 *
 * One hook, so the twelve screens that have to obey it cannot each re-derive it
 * and drift - which is exactly what happened with commercials before the hook
 * below existed. */
export function useSeesScores(): boolean {
  const source = useSource();
  const { data } = useAsync(() => source.getSession(), [source]);
  const roles = data?.user.roles ?? [];
  return roles.includes('gym_admin') || roles.includes('manager');
}

export function useSeesCommercials(): boolean {
  const source = useSource();
  const { data } = useAsync(() => source.getSession(), [source]);
  return seesCommercials(data?.user.roles, data?.gym.settings);
}
